import { CCol, CRow } from '@coreui/react';
import locale from 'antd/es/date-picker/locale/fr_FR';
import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { changeOpenedModal } from '../../redux/actions/headerModal';
import moment from 'moment';
import LoadingAvatar from '../../components/LoadingAvatar';
import ModalFooter from '../../components/ModalFooter';
import UserAsyncDropDown from '../../components/UserAsyncDropDown';
import { getFullDate12Hr, getStatusId } from '../../helper';
import {
  clearServiceError,
  getServiceRequestCategories,
  getService,
  getServices,
  searchRequests,
  updateService
} from '../../redux/actions/services';
import { getBuildingUsers, setUserSearch } from '../../redux/actions/user';
import { setToast } from '../../redux/actions/window';
import { useTranslation } from 'react-i18next';
import { MdError } from 'react-icons/md';
import { LOADING_BLUE } from '../../utils/colorCode';
import localeEn from 'antd/es/date-picker/locale/en_US';
import i18next from 'i18next';
import { STATUS_LABEL_ASSIGNED, PinRelatedCategoryId, isOrgWithOrgWideYardiLookup } from './constant';
import { CDropdown, CDropdownMenu, CDropdownItem } from '@coreui/react';
import { RequestStatus, StatusBGVariants } from './constant';
import {
  searchTable,
  setSearchField,
  setSearchPagination,
  setSearchState
} from '../../redux/actions/search';
import { ENTITY } from '../../utils/searchConstant';
import { ModalHeader, StyledModal, StyledModalBody } from '../../components/Modals';
import { Select, SelectItem } from '../../components/ui/Select';
import { InputSeparator, SettingsModalBoldText } from '../settings/SettingsModals';
import TechnicianNotesList from './TechnicianNotesList';
import {
  areTechnicianNotesEqual,
  buildTechnicianNoteItemsPayload,
  cloneTechnicianNoteItems,
  hasEmptyTechnicianNote,
  hasValidTechnicianNotes
} from './technicianNotesUtils';
import styled from 'styled-components';
import { Fonts, Palette } from '../../components/utils';
import { DropdownToggle, StyledDateText } from './ServiceComponents';
import { CustomDatePicker } from '../../components/CustomSearchBar';
import { SEARCH_TABLES } from '../../helper/constants';
import { requestFail, requestPending, requestSuccess } from '../../utils/status';
import {
  BUILDING_USERS,
  GET_SERVICE,
  GET_SERVICE_CATEGORIES,
  GET_USER
} from '../../redux/constants';
import { isYardiOrganization } from '../users/constant';
import { filterCategoriesForServiceRequestDropdown } from './categories';

const ServiceDetailEdit = () => {
  const dispatch = useDispatch();
  const [completed, setCompleted] = useState(false);
  const { modal } = useSelector((state) => state.headerModal);
  const { user, status: userStatus } = useSelector((state) => state.user);

  const service = useSelector((state) => state.services.service);
  const categories = useSelector((state) => state.services.categories);
  const serviceLoadingStatus = useSelector((state) => state.services.serviceStatus);
  const serviceCategoriesStatus = useSelector((state) => state.services.serviceCategoriesStatus);
  const error = useSelector((state) => state.services.error);
  const searchStatus = useSelector((state) => state.services.searchStatus);
  const search = useSelector((state) => state.services.search);

  const searchState = useSelector((state) => state.search);

  const { globalOrgId, organization } = useSelector((state) => state.Organization);
  const { globalBuildingId, allBuildings } = useSelector((state) => state.building);
  const { t } = useTranslation();
  const localLang = i18next.resolvedLanguage === 'fr' ? locale : localeEn;
  const servicesLabels = {
    category: t('Category'),
    subject: t('Subject'),
    description: t('Description'),
    contact_method: t('Preferred method of contact'),
    urgency: t('Urgency'),
    organization: t('Organization'),
    building: t('Building'),
    tenant: t('Resident'),
    assigned_to: t('Assigned to'),
    scheduled_on: t('Scheduled on')
  };

  const [date, setDate] = useState('');
  const [isDateChanged, setIsDateChanged] = useState('');
  const [serviceReq, setServiceReq] = useState({
    category_id: null,
    schedule_date: '',
    assigned_to: null,
    status_id: null,
    technician_note_items: []
  });
  const [assignedTechnician, setAssignedTechnician] = useState({});
  const [serviceStatus, setServiceStatus] = useState(service?.status ?? '');
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  const isLoading =
    serviceLoadingStatus === requestPending(GET_SERVICE) ||
    serviceCategoriesStatus === requestPending(GET_SERVICE_CATEGORIES) ||
    (service?.assigned_to > 0 && userStatus !== requestSuccess(GET_USER));

  const isError =
    serviceLoadingStatus === requestFail(GET_SERVICE) ||
    serviceCategoriesStatus === requestFail(GET_SERVICE_CATEGORIES) ||
    userStatus === requestFail(BUILDING_USERS) ||
    userStatus === requestFail(GET_USER);
  

  const isYardiWorkRequestsEnabled = useMemo(() => {
    const workRequestBuilding = allBuildings.find((building) => building.id === service.building_id);

    let buildingForYardiCheck = workRequestBuilding;
    const isYardiBuilding = Boolean(buildingForYardiCheck?.yardi_id);

    // Find fallback Yardi building if workRequestBuilding is not a Yardi building and org-wide lookup is enabled
    if (isYardiOrganization(organization) && buildingForYardiCheck && !isYardiBuilding) {
      if (isOrgWithOrgWideYardiLookup(globalOrgId)) {
        const fallbackYardiBuilding = allBuildings.find(
          (building) =>
            building.org_id === buildingForYardiCheck.org_id &&
            Boolean(building?.yardi_id) &&
            building.id !== buildingForYardiCheck.id
        );
        
        if (fallbackYardiBuilding) {
          buildingForYardiCheck = fallbackYardiBuilding;
        }
      }
    }

    return Boolean(buildingForYardiCheck?.sync_config?.enable_yardi_work_requests);
  }, [allBuildings, service.building_id, organization, globalOrgId]);

  const categoryDropdownOptions = useMemo(
    () => filterCategoriesForServiceRequestDropdown(categories, service?.category_id),
    [categories, service?.category_id]
  );

  useEffect(() => {
    setServiceStatus(service?.status ?? '');
  }, [service]);

  useEffect(() => {
    if ([
      RequestStatus.OPEN,
      RequestStatus.REASSIGNMENT,
      RequestStatus.CALL,
      RequestStatus.SCHEDULED,
      RequestStatus.ON_HOLD,
      RequestStatus.WEB
    ].includes(serviceStatus)) {
      setAssignedTechnician(null);
    }
  }, [serviceStatus]);

  useEffect(() => {
    if (user && Object.keys(user).length) {
      let firstName = user.first_name;
      let lastName = user.last_name;
      if (user.id === 0) {
        firstName = t(user.first_name);
        lastName = t(user.last_name);
      }

      setAssignedTechnician({
        value: user.id,
        label: firstName + ' ' + lastName
      });
    }
  }, [user, t]);

  const submit = () => {
    let myContactInfo = service.contact_info;
    const body = {
      user_id: parseInt(service.user_id),
      building_id: parseInt(service.building_id),
      subject: service.subject,
      description: service.description,
      technician_note_items: buildTechnicianNoteItemsPayload(serviceReq.technician_note_items),
      media: service.media,
      time_slot: service.time_slot ? service.time_slot : null,
      contact_type: service.contact_type,
      priority: service.priority,
      allow_access: service.allow_access,
      has_pets: Boolean(service.has_pets),
      assigned_to: assignedTechnician?.value || 0,
      category_id: serviceReq.category_id,
      schedule_date: serviceReq.schedule_date,
      status_id: getStatusId(serviceStatus),
      data: service.data,

      contact_info: myContactInfo
    };
    dispatch(
      updateService({
        id: service.id,
        body: body,
        success: () => {
          dispatch(
            changeOpenedModal({
              modal: 'serviceDetail'
            })
          );
          dispatch(
            searchTable({
              entity: SEARCH_TABLES.WORK_REQUESTS,
              orgID: organization.id,
              body: {
                search: searchState.search,
                minCreationDate: searchState.minCreationDate,
                maxCreationDate: searchState.maxCreationDate,
                filters: searchState.filters,
                ordering: searchState.ordering,
                pagination: searchState.pagination
              },
              success: (res) => {
                dispatch(
                  setSearchState({
                    currentData: res.data.data ?? [],
                    currentPaginationData: res.data.pagination
                  })
                );

                if (!res.data.data) {
                  dispatch(setUserSearch({ page: 1 }));
                  dispatch(
                    setSearchPagination({
                      page: 1,
                      limit: 20
                    })
                  );
                }
              }
            })
          );
          dispatch(
            setToast({
              toastShow: true,
              toastMessage: t('Service request details have been successfully updated!')
            })
          );
          dispatch(getService({ id: service.id }));
          if (search) {
            dispatch(
              searchRequests({
                query: search,
                page: 1,
                search_col: null,
                building: globalBuildingId,
                org: globalOrgId,
                status: searchStatus
              })
            );
          } else {
            dispatch(
              getServices({
                id: globalBuildingId,
                page: 1,
                orgId: globalOrgId,
                status: searchStatus
              })
            );
          }
          dispatch(setSearchField({ entity: ENTITY.request, value: null }));
        },
        fail: () => {
          dispatch(
            setToast({
              toastShow: true,
              toastMessage: t('Service request update failed!'),
              modal: 'errorToast'
            })
          );
        }
      })
    );
  };

  const onClose = () => {
    setServiceStatus('');
    dispatch(
      changeOpenedModal({
        modal: 'serviceDetail'
      })
    );
    dispatch(clearServiceError());
  };

  const onChange = (e, isInt) => {
    e.persist();

    setServiceReq((prev) => ({
      ...prev,
      [e.target.name]: parseInt(e.target.value)
    }));
  };

  const handleSchedule = (value, dateString) => {
    setDate(value);
    const isoDate = Boolean(value) ? value.utcOffset(0, true).toISOString() : '';
    setServiceReq((prev) => ({
      ...prev,
      schedule_date: value ? isoDate : null
    }));
  };

  useEffect(() => {
    const incommingStatus = service.status && getStatusId(service.status);

    let dateTime = service.scheduled_date ? service.scheduled_date : '';
    if (dateTime !== '') {
      dateTime = dateTime.split('T');
      const date = dateTime[0];
      const time = dateTime[1].split('.')[0];
      setDate(service.scheduled_date ? moment(`${date} ${time}`) : ''); // doing this to populate proper dateTIme in calender
      setIsDateChanged(service.scheduled_date ? service.scheduled_date : '');
    }

    if (service.org_id) {
      dispatch(getServiceRequestCategories({ orgId: service.org_id }));
    }

    if (service.building_id) {
      dispatch(getBuildingUsers({ id: service.building_id }));
    }

    setServiceReq((prev) => ({
      ...prev,
      category_id: parseInt(service.category_id),
      schedule_date: service.scheduled_date,
      status_id: incommingStatus,
      assigned_to: service.assigned_to ? service.assigned_to : 0,
      technician_note_items: cloneTechnicianNoteItems(service.technician_note_items)
    }));
  }, [service]);

  const handleAddNote = () => {
    setServiceReq((prev) => ({
      ...prev,
      technician_note_items: [...prev.technician_note_items, { note: '', is_new: true }]
    }));
  };

  const handleRemoveNote = (index) => {
    setServiceReq((prev) => ({
      ...prev,
      technician_note_items: prev.technician_note_items.filter((_, i) => i !== index)
    }));
  };

  const handleNoteChange = (index, value) => {
    setServiceReq((prev) => {
      const items = [...prev.technician_note_items];
      items[index] = { ...items[index], note: value };
      return { ...prev, technician_note_items: items };
    });
  };

  const isNotesDisabled =
    service.status === RequestStatus.COMPLETE ||
    completed === true ||
    service.status === RequestStatus.ARCHIVED;

  function disabledDate(current) {
    // Can not select days before today
    return current < moment().startOf('day');
  }
  const handleCancel = () => {
    onClose();
  };

  const isCompleteBlocked =
    serviceStatus === RequestStatus.COMPLETE &&
    service.technician_notes_required &&
    !hasValidTechnicianNotes(serviceReq.technician_note_items);

  const hasEmptyNote = hasEmptyTechnicianNote(serviceReq.technician_note_items);

  const isUpdateDisabled = useMemo(() => {
    const isNoFieldUpdated =
      serviceReq.category_id === service.category_id &&
      serviceReq.assigned_to === assignedTechnician?.value &&
      serviceReq.schedule_date === isDateChanged &&
      serviceStatus === service.status &&
      areTechnicianNotesEqual(
        serviceReq.technician_note_items,
        service.technician_note_items
      );
    if (isNoFieldUpdated) return isNoFieldUpdated;

    const isInvalidFields =
      !serviceStatus ||
      serviceReq.category_id <= 0 ||
      (serviceStatus === RequestStatus.IN_PROGRESS && !assignedTechnician?.value);

    return (
      isInvalidFields ||
      service.status === 'Complete' ||
      isCompleteBlocked ||
      hasEmptyNote
    );
  }, [
    serviceReq,
    service,
    assignedTechnician,
    isDateChanged,
    serviceStatus,
    isCompleteBlocked,
    hasEmptyNote
  ]);

  return (
    <StyledModal
      show={modal === 'serviceDetailEdit'}
      onClose={handleCancel}
      closeOnBackdrop={false}>
      <ModalHeader title={t('Edit service request')} />
      <StyledModalBody>
        <div>
          {isLoading ? (
            <LoadingAvatar color={LOADING_BLUE} />
          ) : isError ? (
            <div className="error_for_modal">
              <h4 className="text-danger">{error}</h4>
            </div>
          ) : (
            <>
              <CRow className="imgAndDetailDiv">
                <CCol>
                  <InputSeparator>
                    <SettingsModalBoldText>{service.subject}</SettingsModalBoldText>
                    <StyledDateText>
                      {service.create_date && getFullDate12Hr(service.create_date)}
                    </StyledDateText>
                  </InputSeparator>
                  <InputSeparator cssOverride="margin-bottom: 10px; margin-top: 10px">
                    <SettingsModalBoldText>{t('Reference')}:</SettingsModalBoldText>
                    <StyledDateText>{service.reference || 'None'}</StyledDateText>
                  </InputSeparator>
                </CCol>

                <CCol>
                  <InputSeparator>
                    <SettingsModalBoldText>{t('Updated on:')}</SettingsModalBoldText>
                    <StyledDateText>
                      {service.last_update && getFullDate12Hr(service.last_update)}
                    </StyledDateText>
                  </InputSeparator>
                </CCol>
              </CRow>
              <CRow className="statusDiv">
                <CCol sm="5" className="leftCol">
                  <InputSeparator>
                    <SettingsModalBoldText>{t('Status: ')}</SettingsModalBoldText>
                    <CDropdown variant="btn-group">
                      <DropdownToggle
                        caret
                        color={StatusBGVariants[serviceStatus]}
                        disabled={
                          service.status === RequestStatus.COMPLETE ||
                          service.status === RequestStatus.ARCHIVED
                        }>
                        {t(
                          serviceStatus === RequestStatus.IN_PROGRESS
                            ? STATUS_LABEL_ASSIGNED
                            : serviceStatus
                        )}
                      </DropdownToggle>
                      <StyledDropdown
                        show={isDropdownOpen}
                        style={{ transform: 'translate(0px, 60px)' }}>
                        {Object.values(RequestStatus).map(
                          (val) => {
                            if (val === RequestStatus.ARCHIVED) return null;

                            if (parseInt(service.category_id, 10) === PinRelatedCategoryId && val === RequestStatus.COMPLETE) return null;

                            const yardiStatuses = [
                              RequestStatus.CALL,
                              RequestStatus.SCHEDULED,
                              RequestStatus.ON_HOLD,
                              RequestStatus.REASSIGNMENT,
                              RequestStatus.WEB
                            ];

                            // Show Yardi statuses only if Yardi work request sync is enabled
                            if (yardiStatuses.includes(val) && !isYardiWorkRequestsEnabled) return null;

                            return (
                              <CDropdownItem
                                active={serviceStatus === val}
                                onClick={() => {
                                  setIsDropdownOpen(false);
                                  setServiceStatus(val);
                                }}
                                disabled={
                                  service.status === RequestStatus.COMPLETE ||
                                  service.status === RequestStatus.ARCHIVED
                                }
                                key={`select-${val}`}>
                                {val === RequestStatus.IN_PROGRESS ? STATUS_LABEL_ASSIGNED : val}
                              </CDropdownItem>
                            );
                          }
                        )}
                      </StyledDropdown>
                    </CDropdown>
                  </InputSeparator>
                </CCol>
              </CRow>

              <InputSeparator cssOverride="padding-top: 16px">
                <SettingsModalBoldText>{servicesLabels.category}</SettingsModalBoldText>
                <Select
                  loadingMessage={() => t('Loading...')}
                  disabled={
                    completed === true ||
                    parseInt(service.category_id, 10) === PinRelatedCategoryId ||
                    service.status === RequestStatus.COMPLETE ||
                    service.status === RequestStatus.ARCHIVED
                  }
                  onChange={(value) => {
                    setServiceReq((prev) => ({
                      ...prev,
                      category_id: parseInt(value)
                    }));
                  }}
                  name="category_id"
                  id="category_id"
                  value={serviceReq.category_id ? serviceReq.category_id : 0}>
                  {categoryDropdownOptions.length > 0 &&
                    categoryDropdownOptions.map((item) => {
                      return (
                        <SelectItem
                          key={`${item.ID}-${item.SubCategory}`}
                          value={item.ID}
                          disabled={item.ID === PinRelatedCategoryId}>
                          {item.Name}
                          {item?.SubCategory !== '' ? `/${item.SubCategory}` : ''}
                        </SelectItem>
                      );
                    })}
                </Select>
              </InputSeparator>

              <InputSeparator cssOverride="padding-top: 16px">
                <SettingsModalBoldText>{servicesLabels.assigned_to}</SettingsModalBoldText>
                <UserAsyncDropDown
                  skip_deleted={true}
                  disabled={
                    service.status === RequestStatus.COMPLETE ||
                    completed === true ||
                    serviceStatus !== RequestStatus.IN_PROGRESS ||
                    service.status === RequestStatus.ARCHIVED
                  }
                  value={assignedTechnician}
                  setValue={setAssignedTechnician}
                  type={2}
                  buildingId={service.building_id}
                  height={'90px'}
                  asyncPaginateStyles={{ height: '150px' }}
                  placeholder={t('Please select')}
                  all_accessible
                />
              </InputSeparator>

              {!isUpdateDisabled && serviceStatus === RequestStatus.COMPLETE && (
                <StyledWarningText>
                  Once service request is updated to 'Complete', it will not be reversible
                </StyledWarningText>
              )}

              {assignedTechnician?.value > 0 && (
                <InputSeparator cssOverride="padding-top: 16px">
                  <SettingsModalBoldText>{servicesLabels.scheduled_on}</SettingsModalBoldText>
                  <CustomDatePicker
                    width={'100%'}
                    height={'56px'}
                    cssOverride={'padding: 0 24px;'}
                    disabled={
                      service.status === RequestStatus.COMPLETE ||
                      service.status === RequestStatus.ARCHIVED
                    }
                    disabledDate={disabledDate}
                    showTime
                    value={date}
                    name="schedule_date"
                    use12Hours
                    showSecond={false}
                    onChange={handleSchedule}
                    format="YYYY-MM-DD hh:mm A"
                    placeholder={t('Select date')}
                    getPopupContainer={(trigger) => trigger.parentNode}
                    locale={{
                      ...localLang,
                      lang: {
                        ...localLang.lang,
                        ok: 'OK'
                      }
                    }}
                  />
                </InputSeparator>
              )}

              <InputSeparator cssOverride="padding-top: 16px">
                <TechnicianNotesList
                  items={serviceReq.technician_note_items}
                  onAdd={handleAddNote}
                  onRemove={handleRemoveNote}
                  onNoteChange={handleNoteChange}
                  disabled={isNotesDisabled}
                />
              </InputSeparator>

              {isCompleteBlocked && (
                <StyledErrorText>
                  <MdError size={24} />
                  {t('Technician notes are required.')}
                </StyledErrorText>
              )}
            </>
          )}
        </div>
      </StyledModalBody>
      {!isLoading && !isError && (
        <ModalFooter
          text={t('Update')}
          handleCancel={handleCancel}
          className={isUpdateDisabled ? 'btn disable' : 'btn  btn-primary'}
          disabled={isUpdateDisabled}
          onClick={submit}
          status={serviceLoadingStatus === requestPending(GET_SERVICE)}
        />
      )}
    </StyledModal>
  );
};

const StyledDropdown = styled(CDropdownMenu)`
  transform: translate(0px, 40px) !important;
`;

const StyledWarningText = styled.p`
  color: ${Palette.BLACK};
  font-family: ${Fonts.OPEN_SANS_FONT};
  font-size: 14px;
  font-style: normal;
  font-weight: 600;
  line-height: 16px;
  margin-top: 12px;
  margin-bottom: 0;
`;

const StyledErrorText = styled.p`
  display: flex;
  align-items: center;
  gap: 8px;
  color: ${Palette.ERROR};
  font-family: ${Fonts.OPEN_SANS_FONT};
  font-size: 12px;
  font-weight: 600;
  margin-top: 12px;
`;

export default ServiceDetailEdit;
